import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import { toastSuccess } from 'utils/toast'

import { getAllSegments } from 'features/BannerAndTicker/sevices/banner'

import {
  HomeTabCreateEditFormType,
  HomeTabDetailType,
  HomeTabOptionType,
  HomeTabSegmentOptionType,
} from 'features/MultiCategory/@types/homeTabCreateEdit'
import {
  ALL_HUB_ID,
  HOME_TAB_CATEGORY,
  HOME_TAB_SEGMENTATION_TYPE_ID,
  HOME_TAB_TARGETING_TYPE,
} from 'features/MultiCategory/constant/homeTabCreateEdit'
import { toLocationIds } from 'features/MultiCategory/pages/MutlicategoryHomepageCreateEdit/_components/LocationHubs'
import { wibToUtcRfc3339 } from 'features/MultiCategory/utils/date'
import {
  createHomeTab as createHomeTabService,
  CreateHomeTabPayloadType,
  getHomeTabDetail,
  HomeTabDetailResponseType,
  HomeTabImagePayloadType,
  updateHomeTab as updateHomeTabService,
} from 'features/MultiCategory/services/homeTabCreateEdit'
import { getHomeTabTypes } from 'features/MultiCategory/services/homeTabOptions'
import {
  SLICE_NAME,
  setDetail,
  setIsLoading,
  setIsSubmitting,
  setSegmentOptions,
  setTabbingTypeOptions,
} from './slice'
import { resolveIconFileRule, uploadHomeTabIcons } from './thunkIcons'

type HomeTabDetailDataType = HomeTabDetailResponseType['data']

const mapHomeTabDetail = (data: HomeTabDetailDataType): HomeTabDetailType => ({
  id: data.id,
  name: data.name || '',
  displayName: data.display_name || '',
  typeId: data.homepage_tabbing_type_id,
  tabbingCategory: data.tabbing_category,
  slot: data.slot,
  position: data.position,
  status: data.status,
  targetingTypeId: data.targeting_type_id,
  targetedUserIds: data.targeted_user_ids || [],
  targetedSegmentations: (data.targeted_segmentations || []).map((segmentation) => ({
    id: segmentation.id,
    typeId: segmentation.type_id,
    segmentTypeId: segmentation.segment_type_id,
    segmentTypeName: segmentation.segment_type_name,
  })),
  isRecurringSchedule: Boolean(data.is_recurring_schedule),
  startDateTime: data.start_date_time || '',
  endDateTime: data.end_date_time || '',
  images: (data.images || []).map((image) => ({
    id: image.id,
    imageType: image.image_type,
    urlSmall: image.url_small,
    urlMedium: image.url_medium,
    urlLarge: image.url_large,
  })),
  /* `[0]` from the API means every hub; the form turns that into its own sentinel. */
  locationIds: data.location_ids || [],
  /* API sorts by `position ASC, id ASC`; sorted again so the UI never depends on it. */
  components: [...(data.components || [])]
    .sort((left, right) => left.position - right.position || left.id - right.id)
    .map((component) => ({
      id: component.id,
      name: component.component_name,
      typeId: component.homepage_tabbing_component_type_id,
      typeName: component.homepage_tabbing_component_type_name,
      position: component.position,
      startDateTime: component.start_date_time || '',
      endDateTime: component.end_date_time || '',
      isAllTabOnly: Boolean(component.all_tab_only),
    })),
})

export const fetchHomeTabDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchHomeTabDetail`,
  async (id: string, { dispatch }) => {
    /* API rejects ids <= 0 with 400 — skip the guaranteed failure. */
    if (!(Number(id) > 0)) {
      dispatch(setDetail(null))
      return
    }

    dispatch(setIsLoading(true))

    try {
      const {
        data: { data },
      } = await getHomeTabDetail(id)

      dispatch(setDetail(mapHomeTabDetail(data)))
    } catch (error) {
      callErrorMsg(error)
      dispatch(setDetail(null))
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

/** `demand_expansion` → `Demand Expansion`. */
const toOptionLabel = (type: string) =>
  type
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

export const fetchHomeTabTypes = createAsyncThunk(
  `${SLICE_NAME}/fetchHomeTabTypes`,
  async (_: void, { dispatch, getState }) => {
    const {
      multiCategoryHomeTabCreateEdit: { tabbingTypeOptions },
    } = getState() as StoreStateType

    /* Master data — fetch once per page visit. */
    if (tabbingTypeOptions.length) {
      return
    }

    try {
      const {
        data: { data },
      } = await getHomeTabTypes()

      const options: HomeTabOptionType[] = (data || []).map((item) => ({
        name: toOptionLabel(item.type),
        value: String(item.id),
      }))

      dispatch(setTabbingTypeOptions(options))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchHomeTabSegments = createAsyncThunk(
  `${SLICE_NAME}/fetchHomeTabSegments`,
  async (_: void, { dispatch, getState }) => {
    const {
      multiCategoryHomeTabCreateEdit: { segmentOptions },
    } = getState() as StoreStateType

    /* Master data — fetch once per page visit. */
    if (segmentOptions.length) {
      return
    }

    try {
      const { data } = await getAllSegments()

      const options: HomeTabSegmentOptionType[] = (data.data || []).map((segment) => ({
        id: segment.id,
        name: segment.name,
      }))

      dispatch(setSegmentOptions(options))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

const buildTabPayload = (
  form: HomeTabCreateEditFormType,
  images: HomeTabImagePayloadType[],
): CreateHomeTabPayloadType => {
  const tabbingCategory =
    form.variant === 'Standard' ? HOME_TAB_CATEGORY.STANDARD : HOME_TAB_CATEGORY.DEFAULT
  const isDefaultTab = tabbingCategory === HOME_TAB_CATEGORY.DEFAULT
  const startDateTime = wibToUtcRfc3339(form.startSchedule)
  const endDateTime = wibToUtcRfc3339(form.endSchedule)

  /* The Target User section is hidden for default tabs, so any targeting left over
     from a Standard draft must not reach the payload. */
  const targetingTypeId = isDefaultTab
    ? HOME_TAB_TARGETING_TYPE.NORMAL_USER
    : Number(form.targetedUser) || HOME_TAB_TARGETING_TYPE.NORMAL_USER
  const isTargetedUser = targetingTypeId === HOME_TAB_TARGETING_TYPE.TARGETED_USER
  const isSegmentedUser = targetingTypeId === HOME_TAB_TARGETING_TYPE.SEGMENTED_USER

  return {
    name: form.tabbingName.trim(),
    display_name: form.tabbingDisplayName.trim(),
    homepage_tabbing_type_id: Number(form.tabbingType) || 0,
    tabbing_category: tabbingCategory,
    slot: Number(form.tabbingSlot) || 0,
    /* 0 lets the API append the tab at the end of the slot. */
    position: 0,
    targeting_type_id: targetingTypeId,
    targeted_user_ids: isTargetedUser ? form.targetedCustomers.map(({ id }) => Number(id)) : [],
    /* Default tabs must not carry a schedule. */
    ...(isDefaultTab || !startDateTime ? {} : { start_date_time: startDateTime }),
    ...(isDefaultTab || !endDateTime ? {} : { end_date_time: endDateTime }),
    is_recurring_schedule: isDefaultTab ? false : form.scheduleType === 'recurring',
    images,
    targeted_segmentations: isSegmentedUser
      ? form.targetedSegments.map(({ id }) => ({
          type_id: HOME_TAB_SEGMENTATION_TYPE_ID,
          segment_type_id: id,
        }))
      : [],
    /* The hub picker is hidden for default tabs, which always run on every hub,
       so any selection left over from a Standard draft is dropped here. */
    location_ids: isDefaultTab ? [ALL_HUB_ID] : toLocationIds(form.hubs),
  }
}

export const createHomeTab = createAsyncThunk(
  `${SLICE_NAME}/createHomeTab`,
  async (form: HomeTabCreateEditFormType, { dispatch, getState, rejectWithValue }) => {
    const {
      multiCategoryHomeTabCreateEdit: { iconFileRule },
    } = getState() as StoreStateType

    dispatch(setIsSubmitting(true))

    try {
      const fileRules = await resolveIconFileRule(iconFileRule)
      const images = await uploadHomeTabIcons(form, fileRules)
      const {
        data: { data },
      } = await createHomeTabService(buildTabPayload(form, images))

      toastSuccess('Home tab berhasil dibuat sebagai draft')

      return data.id
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    } finally {
      dispatch(setIsSubmitting(false))
    }
  },
)

export type UpdateHomeTabArgsType = {
  form: HomeTabCreateEditFormType
  /** Overrides the tab's current status, e.g. `ACTIVE` when Susunan Komponen is saved. */
  status?: number
}

export const updateHomeTab = createAsyncThunk(
  `${SLICE_NAME}/updateHomeTab`,
  async ({ form, status }: UpdateHomeTabArgsType, { dispatch, getState, rejectWithValue }) => {
    const {
      multiCategoryHomeTabCreateEdit: { detail, iconFileRule },
    } = getState() as StoreStateType

    if (!detail) {
      return rejectWithValue('Detail home tab belum termuat')
    }

    dispatch(setIsSubmitting(true))

    try {
      const fileRules = await resolveIconFileRule(iconFileRule)
      /* Update replaces every image, so untouched icons are resent from the detail. */
      const images = await uploadHomeTabIcons(form, fileRules, detail.images)
      const {
        data: { data },
      } = await updateHomeTabService({
        ...buildTabPayload(form, images),
        id: detail.id,
        /* Position is owned by other flows; status only changes when the caller says so. */
        status: status ?? detail.status,
        position: detail.position,
      })

      toastSuccess('Perubahan home tab berhasil disimpan')

      return data.id
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    } finally {
      dispatch(setIsSubmitting(false))
    }
  },
)
